import { Component, computed, input, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTimepickerModule } from '@angular/material/timepicker';
import moment, { Moment } from 'moment';
import { MatInputModule } from '@angular/material/input';
@Component({
  selector: 'adm-date-with-time-selector',
  imports: [ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatDatepickerModule, MatTimepickerModule],
  templateUrl: './date-with-time-selector.component.html',
  styleUrl: './date-with-time-selector.component.scss',
})
export class DateWithTimeSelectorComponent implements OnInit {
  formItem = input.required<FormControl<Date>>();

  selectedDate = signal<Moment>(moment().startOf('day'));
  selectedTime = signal<Moment>(moment().startOf('hour').add(5, 'minutes'));

  minDate: Moment = moment().startOf('day');
  minDateTime: Moment = moment().startOf('hour').add(5, 'minutes');

  // Start time for the time picker - updated dynamically based on selected date
  minTime = computed(() => {
    const selectedDate = this.selectedDate();
    const isToday = selectedDate.isSame(moment(), 'day');
    return isToday ? this.minDateTime : moment().startOf('day');
  });

  private dateValidator(control: FormControl): { [key: string]: boolean } | null {
    const value = control.value;
    if (value && !moment(value, moment.ISO_8601, true).isValid()) {
      return { invalidDate: true };
    }
    return null;
  }

  private timeValidatorFn = (control: FormControl): { [key: string]: boolean } | null => {
    const timeValue = control.value;
    const selectedDate = this.selectedDate();

    if (!timeValue || !selectedDate) return null;

    const isToday = selectedDate.isSame(moment(), 'day');

    // If selected date is today, validate that time is >= minDateTime
    if (isToday && timeValue.isBefore(this.minDateTime)) {
      return { timeBeforeMinimum: true };
    }

    return null;
  };

  formGroup = new FormGroup({
    whenToSendInviteDate: new FormControl<Moment>(this.selectedDate(), [Validators.required, this.dateValidator]),
    whenToSendInviteTime: new FormControl<Moment>(this.selectedTime(), [Validators.required, this.timeValidatorFn]),
  });

  ngOnInit() {
    // Initialize from parent formItem if it has a value
    const initialValue = this.formItem().value;
    if (initialValue) {
      const momentValue = moment(initialValue);
      this.selectedDate.set(momentValue.clone().startOf('day'));
      this.selectedTime.set(momentValue);
      this.formGroup.controls['whenToSendInviteDate'].setValue(momentValue.clone().startOf('day'), { emitEvent: false });
      this.formGroup.controls['whenToSendInviteTime'].setValue(momentValue, { emitEvent: false });
    }

    // Listen to date changes and update parent formItem
    this.formGroup.controls['whenToSendInviteDate'].valueChanges.subscribe(date => {
      if (date) {
        this.selectedDate.set(date);
        this.formGroup.controls['whenToSendInviteTime'].setValidators([Validators.required, this.timeValidatorFn]);
        this.formGroup.controls['whenToSendInviteTime'].updateValueAndValidity();
        this.updateParentFormItem();
      }
    });

    // Listen to time changes and update parent formItem
    this.formGroup.controls['whenToSendInviteTime'].valueChanges.subscribe(time => {
      if (time) {
        this.selectedTime.set(time);
        this.updateParentFormItem();
      }
    });
  }

  private updateParentFormItem() {
    const date = this.formGroup.controls['whenToSendInviteDate'].value;
    const time = this.formGroup.controls['whenToSendInviteTime'].value;

    if (date && time) {
      // Combine date and time into a single moment object
      const combined = date.clone().hour(time.hour()).minute(time.minute()).second(0).millisecond(0);

      this.formItem().setValue(combined.toDate());
      this.formItem().markAsTouched();
    }
  }

  timeFilter = computed(() => {
    const selectedDate = this.selectedDate();
    const isToday = selectedDate.isSame(moment(), 'day');

    return (time: Moment): boolean => {
      if (!selectedDate) return true;

      if (isToday) {
        return time.isSameOrAfter(this.minDateTime);
      }
      return true;
    };
  });

  public dateFilter = (date: Moment | null): boolean => {
    return date ? date.isSameOrAfter(this.minDate) : false;
  };

  public getTimeErrorMessage(): string {
    const control = this.formGroup.controls['whenToSendInviteTime'];
    if (control.hasError('required')) {
      return 'Vælg et tidspunkt.';
    }
    if (control.hasError('timeBeforeMinimum')) {
      return `Tidspunktet skal være efter ${this.minDateTime.format('HH:mm')} for i dag.`;
    }
    return '';
  }
}
